<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { type CharacterActionLog } from '@/api/dx-backend';
import {
  PHASE_ACTION,
  actorName,
  amountOfAction,
  involvesCharacter,
  phaseOfAction,
  primaryTargetOf,
  toneOfAction
} from './actionLogShared';

const props = withDefaults(defineProps<{
  action: CharacterActionLog,
  selfCharacterId?: string | null
}>(), {
  selfCharacterId: null
});

const emit = defineEmits<{
  'open': [];
}>();

const { t, te } = useI18n();

const tone = computed(() => toneOfAction(props.action));
const amount = computed(() => amountOfAction(props.action));
const targetId = computed(() => primaryTargetOf(props.action));
const isSelf = computed(() => involvesCharacter(props.action, props.selfCharacterId));
const phase = computed(() => phaseOfAction(props.action));
const isActionPhase = computed(() => phase.value === PHASE_ACTION);

const nameOf = (charId: string | null | undefined): string => {
  if (charId && charId === props.selfCharacterId) return t('actionLog.you');
  return actorName(charId) || t('actionLog.unknownCharacter');
};

const actor = computed(() => nameOf(props.action.initiator));
const target = computed(() => nameOf(targetId.value));

const kind = computed(() => {
  const key = `actionTypeEnumPerformed.${props.action.action_type}`;
  if (te(key)) return t(key);
  return isActionPhase.value ? t('actionLog.phases.action') : t('actionLog.phases.preparation');
});

const rowLabel = computed(() => {
  const who = targetId.value ? `${actor.value} → ${target.value}` : actor.value;
  return `${kind.value}: ${who}`;
});
</script>

<template>
  <div
      class="entry"
      :class="[`tone-${tone}`, { 'is-self': isSelf, 'is-prep': !isActionPhase }]"
      role="button"
      tabindex="0"
      :aria-label="rowLabel"
      :title="rowLabel"
      @click="emit('open')"
      @keydown.enter.prevent="emit('open')"
      @keydown.space.prevent="emit('open')"
  >
    <div class="entry-main">
      <span class="entry-kind">{{ kind }}</span>

      <span class="entry-value">
        <template v-if="amount">{{ amount.text }}</template>
      </span>

      <span class="entry-body">
        <span class="entry-bullet" aria-hidden="true"></span>
        <span class="entry-text">
          <span class="entry-actor">{{ actor }}</span>
          <template v-if="targetId">
            <span class="entry-arrow">→</span>
            <span class="entry-actor">{{ target }}</span>
          </template>
        </span>
      </span>
    </div>
  </div>
</template>

<style scoped>
.entry {
  display: flex;
  min-width: 0;
  padding-left: calc(var(--codex-spine-w, 16px) + var(--codex-spine-gap, 1.2rem));
  padding-top: 0.34rem;
  padding-bottom: 0.34rem;
  cursor: pointer;
  opacity: 0.82;
  transition: opacity 0.14s ease;
}

.entry.is-self {
  opacity: 1;
}

.entry:hover,
.entry:focus-visible {
  opacity: 1;
  outline: none;
}

.entry:focus-visible .entry-text {
  outline: 1px solid var(--lg-gold);
  outline-offset: 2px;
}

.entry-main {
  display: grid;
  flex: 1 1 auto;
  min-width: 0;
  grid-template-areas:
      "kind value"
      "body value";
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  row-gap: 0.12rem;
  column-gap: 0.5rem;
}

.entry-kind {
  grid-area: kind;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.63rem;
  line-height: 1.1;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(150, 190, 200, 0.92);
  text-shadow: var(--codex-halo, 0 1px 2px rgba(0, 10, 14, 0.95), 0 0 8px rgba(0, 10, 14, 0.85));
}

.entry-value {
  grid-area: value;
  align-self: center;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.68rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: rgba(210, 218, 224, 0.95);
  text-shadow: var(--codex-halo, 0 1px 2px rgba(0, 10, 14, 0.95), 0 0 8px rgba(0, 10, 14, 0.85));
}

.entry-body {
  grid-area: body;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  min-width: 0;
}

.entry-bullet {
  flex: 0 0 6px;
  width: 6px;
  height: 6px;
  transform: rotate(45deg) translateY(-1px);
  background: #30e2ea;
  box-shadow: 0 0 0.35rem rgba(48, 226, 234, 0.7), 0 1px 3px rgba(0, 10, 14, 0.9);
}

.entry-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.82rem;
  line-height: 1.15;
  color: rgba(196, 208, 215, 0.92);
  text-shadow: var(--codex-halo, 0 1px 2px rgba(0, 10, 14, 0.95), 0 0 8px rgba(0, 10, 14, 0.85));
}

.entry.is-self .entry-text {
  color: #d6e2e9;
}

.entry-arrow {
  margin: 0 0.25rem;
  opacity: 0.5;
}

.entry.is-prep .entry-bullet {
  opacity: 0.45;
}

.tone-damage .entry-bullet { background: var(--lg-dmg); box-shadow: 0 0 0.35rem rgba(255, 77, 94, 0.6); }
.tone-heal .entry-bullet { background: var(--lg-heal); box-shadow: 0 0 0.35rem rgba(74, 222, 128, 0.6); }
.tone-buff .entry-bullet { background: var(--lg-buff); box-shadow: 0 0 0.35rem rgba(255, 209, 71, 0.6); }
.tone-debuff .entry-bullet { background: var(--lg-debuff); box-shadow: 0 0 0.35rem rgba(199, 125, 255, 0.6); }

.tone-damage .entry-value { color: var(--lg-dmg); }
.tone-heal .entry-value { color: var(--lg-heal); }
.tone-buff .entry-value { color: var(--lg-buff); }
.tone-debuff .entry-value { color: var(--lg-debuff); }

@media (prefers-reduced-motion: reduce) {
  .entry {
    transition: none;
  }
}
</style>
